import { useEffect, useState } from "react";
import httpService from "../../services/httpService";
import Button from 'react-bootstrap/Button';
import Badge from "react-bootstrap/Badge";
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import InputGroup from 'react-bootstrap/InputGroup';
import ClubDetailsModal from "./ClubDetailsModal";
import { FaCheckCircle, FaSearch, FaInfoCircle, FaCompass } from "react-icons/fa";
import LoadingFade from "../LoadingFade";
import { getImage } from "../../utils/podcastClubUtils";
import "../../styles/ClubsToJoin.scss";

const ListenerPublicClubs = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [publicClubs, setPublicClubs] = useState([]);
    const [showClubDetailsModal, setShowClubDetailsModal] = useState(false);
    const [selectedClubGuid, setSelectedClubGuid] = useState(null);

    const [search, setSearch] = useState("");
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(12);
    const [orderBy, setOrderBy] = useState("created_at");
    const [sortDirection, setSortDirection] = useState("desc");
    const [onlyFuture, setOnlyFuture] = useState(false);
    const [pagination, setPagination] = useState({
        total: 0, totalPages: 0, hasNextPage: false, hasPreviousPage: false,
    });

    const fetchPublicClubs = () => {
        setLoading(true);
        const params = {
            pageNumber, pageSize,
            order_by: orderBy,
            sort_direction: sortDirection,
            only_future: onlyFuture,
            ...(search && { search }),
        };
        httpService.get("/dash/get-public-clubs", { params })
            .then((res) => {
                setPublicClubs(res.data.data);
                setPagination(res.data.pagination);
            })
            .catch((err) => setError(err.response?.data?.message || "Error loading clubs"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchPublicClubs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNumber, pageSize, orderBy, sortDirection, onlyFuture]);

    const handleReadMore = (clubGuid) => {
        setSelectedClubGuid(clubGuid);
        setShowClubDetailsModal(true);
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setPageNumber(1);
        fetchPublicClubs();
    };

    return (
        <>
            <LoadingFade loading={loading} stateKey={loading ? 'loading' : 'loaded'}>
                <div>
                    {/* ── Toolbar ── */}
                    <Row className="mb-4 align-items-center g-2">
                        <Col md={4}>
                            <Form onSubmit={handleSearchSubmit}>
                                <InputGroup size="sm">
                                    <Form.Control
                                        type="text"
                                        placeholder="Search clubs…"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                    <Button variant="primary" type="submit">
                                        <FaSearch />
                                    </Button>
                                </InputGroup>
                            </Form>
                        </Col>
                        <Col md={3} className="d-flex gap-2">
                            <Form.Select
                                size="sm"
                                value={orderBy}
                                onChange={(e) => { setOrderBy(e.target.value); setPageNumber(1); }}
                            >
                                <option value="created_at">Date Created</option>
                                <option value="start_date">Start Date</option>
                            </Form.Select>
                            <Form.Select
                                size="sm"
                                value={sortDirection}
                                onChange={(e) => { setSortDirection(e.target.value); setPageNumber(1); }}
                                style={{ width: '70px' }}
                            >
                                <option value="desc">↓</option>
                                <option value="asc">↑</option>
                            </Form.Select>
                        </Col>
                        <Col md={3} className="d-flex align-items-center">
                            <Form.Check
                                type="switch"
                                id="only-future-switch"
                                label="Future only"
                                checked={onlyFuture}
                                onChange={(e) => { setOnlyFuture(e.target.checked); setPageNumber(1); }}
                            />
                        </Col>
                        <Col md={2} className="d-flex justify-content-end">
                            <Form.Select
                                size="sm"
                                value={pageSize}
                                onChange={(e) => { setPageSize(parseInt(e.target.value)); setPageNumber(1); }}
                                style={{ width: 'auto' }}
                            >
                                <option value="6">6</option>
                                <option value="12">12</option>
                                <option value="30">30</option>
                            </Form.Select>
                        </Col>
                    </Row>

                    {error && <div className="alert alert-danger">{error}</div>}

                    {/* ── Cards grid ── */}
                    {publicClubs.length > 0 ? (
                        <>
                            <Row className="g-3">
                                {publicClubs.map((club) => {
                                    const img = getImage(club, 'super_wide') || getImage(club, 'tall');
                                    return (
                                        <Col key={club.id} lg={4} md={6}>
                                            <Card className="discover-card h-100">

                                                {/* Cover image */}
                                                <div className="discover-card__img-wrap">
                                                    {img
                                                        ? <img src={img} alt={club.title} className="discover-card__img" />
                                                        : <div className="discover-card__img-placeholder" />
                                                    }
                                                    <span className={`discover-card__pill discover-card__pill--${club.is_subscribed ? 'subscribed' : 'open'}`}>
                                                        <FaCheckCircle />
                                                        {club.is_subscribed ? 'Subscribed' : 'Open'}
                                                    </span>
                                                </div>

                                                {/* Body */}
                                                <Card.Body className="d-flex flex-column p-3">
                                                    <div className="discover-card__title">{club.title}</div>
                                                    {club.description && (
                                                        <p className="discover-card__desc mt-1">{club.description}</p>
                                                    )}

                                                    {/* Skill badges */}
                                                    {club.skills && club.skills.length > 0 && (
                                                        <div className="d-flex flex-wrap gap-1 mt-2">
                                                            {club.skills.map(s => (
                                                                <Badge
                                                                    key={s.id}
                                                                    bg="light"
                                                                    text="dark"
                                                                    className="border"
                                                                    style={{ fontSize: '0.67rem', fontWeight: 500 }}
                                                                >
                                                                    {s.title}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    )}
                                                </Card.Body>

                                                {/* Footer */}
                                                <Card.Footer className="discover-card__footer">
                                                    <span />
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => handleReadMore(club.guid)}
                                                    >
                                                        <FaInfoCircle className="me-1" />
                                                        Details
                                                    </Button>
                                                </Card.Footer>

                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>

                            {/* Pagination */}
                            <div className="ctj-pagination">
                                <span>
                                    Showing {(pageNumber - 1) * pageSize + 1}–{Math.min(pageNumber * pageSize, pagination.total)} of {pagination.total} clubs
                                </span>
                                <div className="d-flex align-items-center gap-2">
                                    <Button
                                        variant="outline-primary" size="sm"
                                        onClick={() => setPageNumber(p => p - 1)}
                                        disabled={!pagination.hasPreviousPage}
                                    >
                                        Previous
                                    </Button>
                                    <span>Page {pageNumber} of {pagination.totalPages}</span>
                                    <Button
                                        variant="outline-primary" size="sm"
                                        onClick={() => setPageNumber(p => p + 1)}
                                        disabled={!pagination.hasNextPage}
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="ctj-empty">
                            <FaCompass className="ctj-empty__icon" />
                            <p className="ctj-empty__text">No clubs found. Try adjusting your search criteria.</p>
                        </div>
                    )}
                </div>
            </LoadingFade>

            <ClubDetailsModal
                show={showClubDetailsModal}
                onHide={() => { setShowClubDetailsModal(false); setSelectedClubGuid(null); }}
                clubGuid={selectedClubGuid}
            />
        </>
    );
};

export default ListenerPublicClubs;
